// Generate smooth bezier curve path from data points
// Uses Catmull-Rom to Bezier conversion for natural curves
function generateCurvePath(points, width, height) {
  if (!points || points.length === 0) return '';

  // Handle single point: create a flat rectangle across full width
  if (points.length === 1) {
    const y = height - points[0] * height;
    return `M 0,${height} L 0,${y} L ${width},${y} L ${width},${height} Z`;
  }

  // Convert data points to coordinates
  const coords = points.map((value, i) => ({
    x: (i / (points.length - 1)) * width,
    y: height - value * height, // Invert Y (SVG 0 is top)
  }));

  // Start path at bottom-left, line to first point
  let path = `M 0,${height} L ${coords[0].x},${coords[0].y}`;

  // Generate smooth bezier curves through points
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i - 1] || coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[i + 2] || p2;

    // Catmull-Rom to Bezier control points (lower = less rounded)
    const tension = 0.08;
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  // Close path: line to bottom-right, then back to start
  path += ` L ${width},${height} Z`;

  return path;
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static getOverlayTemplateContent(html) {
    return html.querySelector('template[data-overlay-template="mobile-filters"]')?.content;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const overlayTemplateContent = FacetFiltersForm.getOverlayTemplateContent(parsedHTML);

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    const overlayFiltersFromFetch = overlayTemplateContent?.querySelectorAll("#FacetFiltersFormOverlay .js-filter") || [];

    const allFetchedFilters = [...facetDetailsElementsFromFetch, ...overlayFiltersFromFetch];

    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersFormOverlay .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(allFetchedFilters).some(
          ({ id }) => currentElement.id === id,
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(allFetchedFilters).filter(
      (element) => !matchesId(element),
    );
    const countsToRender = Array.from(allFetchedFilters).find(
      matchesId,
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        const hasPriceRange = currentElement.querySelector('price-range');
        const hasSingleRange = currentElement.querySelector('single-range');

        if (hasPriceRange || hasSingleRange) {
          // Preserve slider state during AJAX updates
        } else {
          document.getElementById(elementToRender.id).innerHTML =
            elementToRender.innerHTML;
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            const previousElement = document.getElementById(previousElementId);
            if (previousElement) {
              previousElement.after(elementToRender);
              return;
            }
          }
        }

        if (elementToRender.parentElement && elementToRender.parentElement.id) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            ?.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderFilterGroupBadges(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter"),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details",
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const overlayTemplateContent = FacetFiltersForm.getOverlayTemplateContent(html);

    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    if (overlayTemplateContent) {
      const overlayActiveFacets = overlayTemplateContent.querySelector(".mobile-filters-overlay__active-facets");
      const targetOverlayActiveFacets = document.querySelector(".mobile-filters-overlay__active-facets");
      if (overlayActiveFacets && targetOverlayActiveFacets) {
        targetOverlayActiveFacets.innerHTML = overlayActiveFacets.innerHTML;
      }
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderFilterGroupBadges(html) {
    const overlayTemplateContent = FacetFiltersForm.getOverlayTemplateContent(html);
    const siteOverlay = document.querySelector('site-overlay');

    function updateBadge(newBadge, targetRoot) {
      const groupId = newBadge.dataset.filterGroupBadge;
      const currentBadge = targetRoot?.querySelector(`[data-filter-group-badge="${groupId}"]`);
      if (!currentBadge) return;

      currentBadge.textContent = newBadge.textContent;
      currentBadge.classList.toggle('hidden', newBadge.classList.contains('hidden'));
    }

    html.querySelectorAll('[data-filter-group-badge]').forEach((badge) => {
      updateBadge(badge, document);
    });

    overlayTemplateContent?.querySelectorAll('[data-filter-group-badge]').forEach((badge) => {
      updateBadge(badge, siteOverlay);
    });
  }

  static syncSortValues(value) {
    // Sync sort value across all sort forms/selects
    const desktopSelect = document.getElementById('SortByToolbar');
    const mobileRadios = document.querySelectorAll('#FacetSortFormMobile input[name="sort_by"]');

    if (desktopSelect) {
      desktopSelect.value = value;
    }

    mobileRadios.forEach((radio) => {
      radio.checked = radio.value === value;
      const label = radio.closest('.mobile-sort-overlay__option');
      if (label) {
        label.classList.toggle('mobile-sort-overlay__option--selected', radio.checked);
      }
    });
  }

  static renderAdditionalElements(html) {
    const overlayTemplateContent = FacetFiltersForm.getOverlayTemplateContent(html);

    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    if (overlayTemplateContent) {
      const overlayCount = overlayTemplateContent.querySelector(".mobile-filters-overlay__count");
      const targetOverlayCount = document.querySelector(".mobile-filters-overlay__count");
      if (overlayCount && targetOverlayCount) {
        targetOverlayCount.innerHTML = overlayCount.innerHTML;
      }
    }

    const desktopSortSelect = html.querySelector('#SortByToolbar');
    if (desktopSortSelect) {
      const currentSortValue = desktopSortSelect.value;
      FacetFiltersForm.syncSortValues(currentSortValue);
    }

    const mobileForm = document.getElementById("FacetFiltersFormMobile");
    const menuDrawer = mobileForm?.closest("menu-drawer");
    if (menuDrawer && typeof menuDrawer.bindEvents === "function") {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden"),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item"),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    const formId = event.target.closest("form")?.id;
    const isMobileOrOverlay = formId === "FacetFiltersFormMobile" || formId === "FacetFiltersFormOverlay";
    const isMobileSortForm = formId === "FacetSortFormMobile";

    // Sync sort values when mobile sort form changes
    if (isMobileSortForm) {
      const selectedValue = event.target.value;
      FacetFiltersForm.syncSortValues(selectedValue);
    }

    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form"),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      sortFilterForms.forEach((form) => {
        if (!isMobileOrOverlay && !isMobileSortForm) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile" || form.id === "FacetFiltersFormOverlay") {
          // Only submit from the form that triggered the event
          if (form.id === formId) {
            forms.push(this.createSearchParams(form));
          }
        } else if (isMobileSortForm && form.id === "FacetSortFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input[data-price-input]").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  connectedCallback() {
    // Wait for next tick to ensure slider script has loaded
    setTimeout(() => {
      this.initializePriceSlider();
    }, 100);
  }

  initializePriceSlider() {
    const sliderContainer = this.querySelector('[data-price-slider]');
    if (!sliderContainer) return;

    // Initialize the price slider (moved from inline script)
    window.PriceSliderInit && window.PriceSliderInit(sliderContainer);
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class SingleRange extends HTMLElement {
  connectedCallback() {
    setTimeout(() => {
      this.initializeSingleSlider();
    }, 100);
  }

  initializeSingleSlider() {
    const sliderContainer = this.querySelector('[data-single-slider]');
    if (!sliderContainer) return;

    const direction = sliderContainer.getAttribute('data-direction');
    const inputsContainer = this.querySelector('[data-slider-inputs]');

    // Get data from attributes
    let filterCounts = null;
    let labels = null;
    let rangeMax = null;
    let isYear = false;

    if (inputsContainer) {
      // Filter counts for histogram
      const countsAttr = inputsContainer.getAttribute('data-filter-counts');
      if (countsAttr) {
        try {
          filterCounts = JSON.parse(countsAttr);
        } catch (e) {}
      }

      // Range max
      rangeMax = parseInt(inputsContainer.getAttribute('data-range-max')) || null;

      // Condition labels
      const isCondition = inputsContainer.getAttribute('data-is-condition') === 'true';
      if (isCondition) {
        const labelsAttr = inputsContainer.getAttribute('data-condition-labels');
        if (labelsAttr) {
          try {
            labels = JSON.parse(labelsAttr);
          } catch (e) {}
        }
      }

      isYear = inputsContainer.getAttribute('data-is-year') === 'true';
    }

    window.SingleRangeSliderInit && window.SingleRangeSliderInit(sliderContainer, direction, labels, rangeMax, filterCounts, isYear);
  }
}

customElements.define("single-range", SingleRange);

window.HistogramHelpers = window.HistogramHelpers || {
  /**
   * Create bar-based histogram for single-handle sliders
   * @param {HTMLElement} container - Container element for histogram
   * @param {Object} counts - Object mapping values to counts
   * @param {string} direction - 'lte' or 'gte' for highlight direction
   * @param {number} activePercent - Current slider position as percentage (0-100)
   * @returns {Object} { bars, updateHighlight } for later updates
   */
  createSmoothHistogram: function(container, counts, direction, activePercent) {
    if (!counts || Object.keys(counts).length === 0) return null;

    const values = Object.keys(counts).map(Number).sort((a, b) => a - b);
    const maxCount = Math.max(...Object.values(counts), 1);
    const barCount = values.length;

    container.style.display = 'flex';
    container.style.alignItems = 'flex-end';
    container.style.gap = '1px';
    container.innerHTML = '';

    const bars = [];
    values.forEach((val, i) => {
      const bar = document.createElement('div');
      bar.className = 'slider-histogram__bar';
      const heightPercent = Math.max(5, (counts[val] / maxCount) * 100);
      bar.style.height = heightPercent + '%';
      bar.dataset.index = i;
      bar.dataset.value = val;

      const barPercent = (i / Math.max(barCount - 1, 1)) * 100;
      const isActive = direction === 'lte'
        ? barPercent <= activePercent
        : barPercent >= activePercent;

      if (isActive) {
        bar.classList.add('slider-histogram__bar--active');
      }

      container.appendChild(bar);
      bars.push({ element: bar, percent: barPercent });
    });

    return {
      bars,
      direction,
      updateHighlight: function(percent) {
        bars.forEach(({ element, percent: barPercent }) => {
          const isActive = direction === 'lte'
            ? barPercent <= percent
            : barPercent >= percent;
          element.classList.toggle('slider-histogram__bar--active', isActive);
        });
      }
    };
  },

  /**
   * Create bar-based histogram for dual-handle price slider
   * @param {HTMLElement} container - Container element
   * @param {Object} counts - Bin counts object
   * @param {number} minPercent - Min slider position (0-100)
   * @param {number} maxPercent - Max slider position (0-100)
   * @returns {Object} { bars, updateHighlight }
   */
  createDualHistogram: function(container, counts, minPercent, maxPercent) {
    if (!counts || Object.keys(counts).length === 0) return null;

    const values = Object.keys(counts).map(Number).sort((a, b) => a - b);
    const maxCount = Math.max(...Object.values(counts), 1);
    const barCount = values.length;

    container.style.display = 'flex';
    container.style.alignItems = 'flex-end';
    container.style.gap = '1px';
    container.innerHTML = '';

    const bars = [];
    values.forEach((val, i) => {
      const bar = document.createElement('div');
      bar.className = 'slider-histogram__bar';
      const heightPercent = Math.max(5, (counts[val] / maxCount) * 100);
      bar.style.height = heightPercent + '%';
      bar.dataset.index = i;

      const barPercent = (i / Math.max(barCount - 1, 1)) * 100;
      const isActive = barPercent >= minPercent && barPercent <= maxPercent;

      if (isActive) {
        bar.classList.add('slider-histogram__bar--active');
      }

      container.appendChild(bar);
      bars.push({ element: bar, percent: barPercent });
    });

    return {
      bars,
      updateHighlight: function(minP, maxP) {
        bars.forEach(({ element, percent }) => {
          const isActive = percent >= minP && percent <= maxP;
          element.classList.toggle('slider-histogram__bar--active', isActive);
        });
      }
    };
  }
};

window.MAX_FILTER_VALUES = window.MAX_FILTER_VALUES || 20;

window.SingleRangeSliderInit = function(container, direction, labels, rangeMax, filterCounts, isYear) {
  if (!container || !container.hasAttribute('data-single-slider')) return;

  isYear = isYear || false;

  if (container.hasAttribute('data-slider-initialized')) return;

  const singleRange = container.closest('single-range');
  if (singleRange && singleRange.offsetParent === null) return;

  container.setAttribute('data-slider-initialized', 'true');

  const slider = container.querySelector('[data-slider]');
  const fill = container.querySelector('[data-slider-fill]');
  const valueDisplay = container.querySelector('[data-value-display]');
  const histogram = container.querySelector('[data-histogram]');
  const inputsContainer = singleRange ? singleRange.querySelector('[data-slider-inputs]') : null;

  if (!slider || !fill || !valueDisplay || !inputsContainer) {
    return;
  }

  const filterValues = JSON.parse(inputsContainer.getAttribute('data-filter-values') || '[]');
  const filterOriginals = JSON.parse(inputsContainer.getAttribute('data-filter-originals') || '[]');
  const paramName = inputsContainer.getAttribute('data-param-name');
  const hasLabels = labels && Object.keys(labels).length > 0;

  const svgWidth = 200;
  const svgHeight = 40;
  let clipRect = null;

  function getClipPosition(percent) {
    if (direction === 'lte') {
      return { x: 0, width: (percent / 100) * svgWidth };
    }
    const clipX = (percent / 100) * svgWidth;
    return { x: clipX, width: svgWidth - clipX };
  }

  function getSliderPercent() {
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const range = max - min;
    if (range === 0) return 100;
    return ((parseInt(slider.value) - min) / range) * 100;
  }

  function initHistogram() {
    if (!histogram || !filterCounts || Object.keys(filterCounts).length === 0) return;

    const values = Object.keys(filterCounts).map(Number).sort((a, b) => a - b);
    const countValues = Object.values(filterCounts).map(Number).filter(n => !isNaN(n) && n > 0);
    const maxCount = countValues.length > 0 ? Math.max(...countValues) : 1;

    if (values.length === 0 || maxCount === 0) return;

    const normalizedValues = values
      .map((key) => Math.max(0.05, (Number(filterCounts[key]) || 0) / maxCount))
      .filter(v => !isNaN(v) && isFinite(v));

    if (normalizedValues.length === 0) return;

    const curvePath = generateCurvePath(normalizedValues, svgWidth, svgHeight);
    if (!curvePath) return;

    const clipId = 'histogram-clip-' + Math.random().toString(36).substring(2, 11);
    const { x: clipX, width: clipWidth } = getClipPosition(getSliderPercent());

    histogram.innerHTML = `
      <svg viewBox="0 0 ${svgWidth} ${svgHeight}" preserveAspectRatio="none" class="slider-histogram__svg">
        <defs>
          <clipPath id="${clipId}">
            <rect x="${clipX}" y="0" width="${clipWidth}" height="${svgHeight}" data-clip-rect />
          </clipPath>
        </defs>
        <path d="${curvePath}" class="slider-histogram__curve slider-histogram__curve--base" />
        <path d="${curvePath}" class="slider-histogram__curve slider-histogram__curve--active" clip-path="url(#${clipId})" />
      </svg>
    `;

    clipRect = histogram.querySelector('[data-clip-rect]');
  }

  initHistogram();

  function updateHistogramHighlight() {
    if (!clipRect) return;
    const { x, width } = getClipPosition(getSliderPercent());
    clipRect.setAttribute('x', x);
    clipRect.setAttribute('width', width);
  }

  let isUpdating = false;

  function formatValue(value) {
    const num = parseInt(value);
    if (isYear) return num.toString();
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  }

  function formatWithLabel(value) {
    const num = parseInt(value);
    if (hasLabels && labels[num]) {
      const label = labels[num];
      // For condition filters: show "Label and up" unless at max value
      const maxVal = rangeMax || parseInt(slider.max);
      if (num < maxVal) {
        return label + ' and up';
      }
      return label;
    }
    return formatValue(value);
  }

  function updateUI() {
    const value = parseInt(slider.value);
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const range = max - min;
    const percent = ((value - min) / range) * 100;

    if (direction === 'lte') {
      fill.style.left = '0%';
      fill.style.width = percent + '%';
    } else {
      fill.style.left = percent + '%';
      fill.style.width = (100 - percent) + '%';
    }

    valueDisplay.textContent = formatWithLabel(value);
    updateHistogramHighlight();
  }

  slider.addEventListener('input', function() {
    updateUI();
  });

  /**
   * Sample filter values using adaptive percentile strategy
   * Guarantees boundaries + even distribution across actual values
   * @param {Array} values - Sorted array of numeric values to sample from
   * @param {Number} maxCount - Maximum number of values to return
   * @returns {Array} Sampled values (may be less than maxCount if input is small)
   */
  function sampleFilterValues(values, maxCount) {
    if (values.length <= maxCount) return values;

    const sampled = [];
    const first = values[0];
    const last = values[values.length - 1];

    sampled.push(first);

    const interiorCount = maxCount - 2;
    const stepSize = (values.length - 1) / (interiorCount + 1);

    for (let i = 1; i <= interiorCount; i++) {
      const index = Math.round(stepSize * i);
      sampled.push(values[index]);
    }

    sampled.push(last);

    return [...new Set(sampled)];
  }

  function updateHiddenInputs(sliderValue) {
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const shouldSubmit = direction === 'lte' ? sliderValue < max : sliderValue > min;

    inputsContainer.innerHTML = '';

    if (!shouldSubmit) return;

    // Collect original string values for matching numeric values
    // (numeric conversion can mangle strings like "1.000" → 1, breaking Shopify's exact-match filter)
    const matchingOriginals = [];
    filterValues.forEach((v, i) => {
      const matches = direction === 'lte' ? v <= sliderValue : v >= sliderValue;
      if (matches && filterOriginals[i] !== undefined) {
        matchingOriginals.push(filterOriginals[i]);
      }
    });

    const valuesToSubmit = sampleFilterValues(matchingOriginals, window.MAX_FILTER_VALUES);

    valuesToSubmit.forEach(value => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = paramName;
      input.value = value;
      inputsContainer.appendChild(input);
    });
  }

  slider.addEventListener('change', function() {
    if (isUpdating) return;
    isUpdating = true;

    const value = parseInt(this.value);
    this.value = value;
    updateUI();
    updateHiddenInputs(value);

    const firstInput = inputsContainer.querySelector('input[type="hidden"]');
    if (firstInput) {
      firstInput.dispatchEvent(new Event('input', { bubbles: true }));
      firstInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    isUpdating = false;
  });

  updateUI();
};

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class FilterToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.filter-toggle__button');
    this.textElement = this.querySelector('.filter-toggle__text');
    this.facetsVertical = document.querySelector('.facets-vertical');

    if (this.button && this.facetsVertical) {
      this.button.addEventListener('click', this.toggle.bind(this));
    }
  }

  toggle() {
    const isHidden = this.facetsVertical.classList.toggle('filters-hidden');

    // Update button text
    if (this.textElement) {
      const hideText = this.textElement.dataset.hideText;
      const showText = this.textElement.dataset.showText;
      this.textElement.textContent = isHidden ? showText : hideText;
    }

    // Update aria-expanded
    this.button.setAttribute('aria-expanded', !isHidden);
  }
}

customElements.define('filter-toggle', FilterToggle);

/**
 * Multi-Color Filter Handler
 * Splits comma-separated color values into individual color filters
 * Example: "Blau, braun" becomes separate "Blau" and "braun" options
 */
class MultiColorFilterHandler {
  constructor(filterElement) {
    this.filterElement = filterElement;
    this.colorItems = Array.from(filterElement.querySelectorAll('.color-filter-card-item'));
    this.init();
  }

  init() {
    // Build map of individual colors to their source checkboxes
    this.colorMap = new Map();

    this.colorItems.forEach(item => {
      const colorValue = item.dataset.colorValue;
      const checkbox = item.querySelector('input[type="checkbox"]');
      const count = parseInt(checkbox.dataset.colorCount) || 0;
      const isMultiColor = item.dataset.isMultiColor === 'true';

      if (!colorValue || !checkbox) return;

      const colors = colorValue.split(',').map(c => c.trim());

      colors.forEach(color => {
        if (!this.colorMap.has(color)) {
          this.colorMap.set(color, {
            name: color,
            count: 0,
            sourceCheckboxes: []
          });
        }

        const colorData = this.colorMap.get(color);
        colorData.count += count;
        colorData.sourceCheckboxes.push({
          checkbox,
          item,
          isMultiColor,
        });
      });
    });

    this.hideMultiColorItems();
    this.setupCheckboxListeners();
  }

  hideMultiColorItems() {
    this.colorItems.forEach(item => {
      if (item.dataset.isMultiColor === 'true') {
        item.style.display = 'none';
      }
    });
  }

  setupCheckboxListeners() {
    // Placeholder for future multi-color checkbox logic.
    // Currently, single-color items handle form submission natively.
  }
}

function initMultiColorFilters() {
  const colorFilters = document.querySelectorAll('.filter-color');
  colorFilters.forEach(filter => {
    new MultiColorFilterHandler(filter);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMultiColorFilters);
} else {
  initMultiColorFilters();
}

const originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;
FacetFiltersForm.renderProductGridContainer = function(html) {
  originalRenderProductGridContainer.call(this, html);
  if (window.initializeProductCards) {
    window.initializeProductCards();
  }
};

const originalRenderFilters = FacetFiltersForm.renderFilters;
FacetFiltersForm.renderFilters = function(html, event) {
  originalRenderFilters.call(this, html, event);

  setTimeout(() => {
    initMultiColorFilters();

    if (window.initFilterCheckboxShowMore) {
      document.querySelectorAll('.filter-checkbox').forEach(fieldset => {
        window.initFilterCheckboxShowMore(fieldset);
      });
    }

    document.querySelectorAll('show-more-button').forEach(element => {
      if (element.connectedCallback) {
        element.connectedCallback();
      }
    });
  }, 0);
};
